<template>
    <div>

        <Head title="Create Accessory" />
        <h1 class="mb-8 text-3xl font-bold">
            <Link class="text-blue-400 hover:text-blue-600" href="/accessories">Accessories</Link>
            <span class="text-blue-400 font-medium">/</span> Create
        </h1>
        <div class="max-w-3xl bg-white shadow overflow-hidden">
            <form @submit.prevent="store">
                <div class="flex flex-wrap -mb-8 -mr-6 p-8">
                    <select-input v-model="form.system" :error="form.errors.system" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="System">
                        <option v-for="s in systems" :key="s.id" :value="s.id"> {{ s.name }}</option>
                    </select-input>
                    <select-input v-model="form.width" :error="form.errors.width" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Width">
                        <option v-for="w in widths" :key="w.id" :value="w.id"> {{ w.in }}" ({{ w.mm }} mm)</option>
                    </select-input>
                    <select-input v-model="form.edge" :error="form.errors.edge" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Edge">
                        <option v-for="e in edges" :key="e.id" :value="e.id"> {{ e.name }}</option>
                    </select-input>
                    <select-input v-model="form.outlet_type" :error="form.errors.outlet_type"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Outlet Type">
                        <option v-for="o in outletTypes" :key="o.id" :value="o.id"> {{ o.name }}</option>
                    </select-input>
                    <text-input v-model="form.part_number" :error="form.errors.part_number"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Part Number" />
                    <text-input v-model="form.outlet_size" :error="form.errors.outlet_size"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Outlet Size (in)" type="number" step="0.1" />
                    <div class="pb-8 pr-6 w-full lg:w-1/2 flex">
                        <checkbox-input v-model="form.required" :error="form.errors.required" label="Required" />
                    </div>
                    <div class="pb-8 pr-6 w-full lg:w-1/2 flex">
                        <checkbox-input v-model="form.shallow" :error="form.errors.shallow" label="Shallow" />
                    </div>
                    <text-input v-model="form.per_channel" :error="form.errors.per_channel"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Per Channel" type="number" />
                    <text-input v-model="form.length_im" :error="form.errors.length_im"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Length Imperial (in)" type="number" step="0.01" />
                    <text-input v-model="form.length" :error="form.errors.length" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Length (mm)" type="number" />
                    <text-input v-model="form.depth_im" :error="form.errors.depth_im" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Depth Imperial (in)" type="number" step="0.01" />
                    <text-input v-model="form.depth" :error="form.errors.depth" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Depth (mm)" type="number" />
                    <text-input v-model="form.weight_kb" :error="form.errors.weight_kb"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Weight (kg)" type="number" step="0.001" />
                    <text-input v-model="form.weight_lbs" :error="form.errors.weight_lbs"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Weight (lbs)" type="number" step="0.001" />
                    <textarea-input v-model="form.description" :error="form.errors.description" class="pb-8 pr-6 w-full"
                        label="Description" />
                    <textarea-input v-model="form.tooltip" :error="form.errors.tooltip" class="pb-8 pr-6 w-full"
                        label="Tooltip" />
                    <text-input v-model="form.calculation_formula" :error="form.errors.calculation_formula"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Calculation Formula" 
                        placeholder="e.g., channels * 2, Math.ceil(length)" />
                    <div class="pb-8 pr-6 w-full lg:w-1/2 flex">
                        <checkbox-input v-model="form.is_optional" :error="form.errors.is_optional" label="Optional Accessory" />
                    </div>
                    <file-input v-model="form.image" :error="form.errors.image" class="pb-8 pr-6 w-full lg:w-1/2"
                        type="file" accept="image/*" label="Image" />
                </div>
                <!-- Price Lists -->
                <div class="mt-6 rounded shadow overflow-x-auto">
                    <table class="w-full whitespace-nowrap">
                        <tr class="text-left font-bold">
                            <th class="pb-4 pt-6 px-6">PriceLists</th>
                            <th class="pb-4 pt-6 px-6">Alias</th>
                            <th class="pb-4 pt-6 px-6">Price</th>
                        </tr>
                        <tr v-for="(pricelist, i) in form.pricelists" :key="pricelist.id"
                            class="hover:bg-gray-100 focus-within:bg-gray-100">
                            <td class="px-6 py-2 focus:text-blue-500">
                                {{ pricelist.name }}
                                <icon v-if="pricelist.deleted_at" name="trash"
                                    class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
                            </td>
                            <td class="px-6 py-2 focus:text-blue-500">
                                <text-input v-model="pricelist.alias" :error="form.errors[`pricelists.${i}.alias`]"
                                    class="w-full" />
                            </td>
                            <td class="px-6 py-2 focus:text-blue-500">
                                <text-input v-model="pricelist.price" :error="form.errors[`pricelists.${i}.price`]"
                                    class="w-full" label="" type="number" min="0" step="0.01" />
                            </td>
                        </tr>
                        <tr v-if="pricelists.length === 0">
                            <td class="px-6 py-4 border-t" colspan="4">No price lists found.</td>
                        </tr>
                    </table>
                </div>
                <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
                    <loading-button :loading="form.processing" class="btn-blue ml-auto" type="submit">Create
                        Accessory</loading-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput'
import LoadingButton from '@/Shared/LoadingButton'
import SelectInput from '@/Shared/SelectInput'
import FileInput from '@/Shared/FileInput'
import CheckboxInput from '@/Shared/CheckboxInput'

export default {
    components: {
        Head,
        Link,
        Icon,
        LoadingButton,
        TextInput,
        TextareaInput,
        SelectInput,
        FileInput,
        CheckboxInput,
    },
    layout: Layout,
    props: {
        systems: Array,
        widths: Array,
        edges: Array,
        outletTypes: Array,
        pricelists: Array,
    },
    remember: 'form',
    data() {
        return {
            form: this.$inertia.form({
                system: null,
                width: null,
                edge: null,
                outlet_type: null,
                part_number: null,
                description: null,
                tooltip: null,
                calculation_formula: null,
                outlet_size: null,
                required: false,
                shallow: false,
                is_optional: true,
                per_channel: null,
                length_im: null,
                length: null,
                depth_im: null,
                depth: null,
                weight_kb: null,
                weight_lbs: null,
                image: null,
                pricelists: this.pricelists,
            }),
        }
    },
    methods: {
        store() {
            this.form.post('/accessories', {
                onSuccess: () => this.form.reset('image'),
            })
        },
    },
}
</script>